import { useState, useRef, useEffect } from 'react';

import { createPortal } from 'react-dom';

interface TooltipOptions {
  content: string;
  delay?: number;
}

enum ArrowDirection {
  LEFT = 'left',
  RIGHT = 'right',
}

interface TooltipPosition {
  x: number;
  y: number;
}

export function useTooltip<T extends HTMLElement = HTMLElement>({
  content,
  delay = 0,
}: TooltipOptions) {
  const [showTooltip, setShowTooltip] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [tooltipPos, setTooltipPos] = useState<TooltipPosition>({
    x: 0,
    y: 0,
  });
  const [arrowDirection, setArrowDirection] = useState<ArrowDirection>(
    ArrowDirection.LEFT,
  );
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);
  const triggerRef = useRef<T>(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (showTooltip && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const tooltipWidth = 208;
      const tooltipHeight = 40;
      const arrowSize = 6;
      const offset = arrowSize + 18;

      let x = rect.right + offset;
      let y = rect.top + rect.height / 2;

      if (x + tooltipWidth > window.innerWidth - 10) {
        x = rect.left - tooltipWidth - offset;
        setArrowDirection(ArrowDirection.RIGHT);
      } else {
        setArrowDirection(ArrowDirection.LEFT);
      }

      if (y + tooltipHeight / 2 > window.innerHeight - 10) {
        y = window.innerHeight - tooltipHeight / 2 - 10;
      }

      if (y - tooltipHeight / 2 < 10) {
        y = tooltipHeight / 2 + 10;
      }

      setTooltipPos({ x, y });
    }
  }, [showTooltip]);

  useEffect(() => {
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [timeoutId]);

  const show = () => {
    if (delay > 0) {
      const id = setTimeout(() => setShowTooltip(true), delay);
      setTimeoutId(id);
    } else {
      setShowTooltip(true);
    }
  };

  const hide = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      setTimeoutId(null);
    }
    setShowTooltip(false);
  };

  const tooltip =
    showTooltip && mounted
      ? createPortal(
          <div
            ref={(el) => {
              if (el) {
                el.style.setProperty('--tooltip-x', `${tooltipPos.x}px`);
                el.style.setProperty('--tooltip-y', `${tooltipPos.y}px`);
                el.style.left = 'var(--tooltip-x)';
                el.style.top = 'var(--tooltip-y)';
              }
            }}
            aria-live="polite"
            className="pointer-events-none fixed z-40 -translate-y-1/2"
            id="favorites-tooltip"
            role="tooltip"
          >
            <div className="relative w-fit rounded-lg bg-blue-800 py-3 pl-5 pr-4 text-sm font-medium text-white shadow-lg">
              <span className="whitespace-nowrap">{content}</span>

              {arrowDirection === ArrowDirection.LEFT && (
                <div className="absolute top-1/2 -left-1.5 h-0 w-0 -translate-y-1/2 border-r-8 border-t-8 border-b-8 border-t-transparent border-b-transparent border-r-blue-800" />
              )}

              {arrowDirection === ArrowDirection.RIGHT && (
                <div className="absolute -right-1.5 top-1/2 h-0 w-0 -translate-y-1/2 border-l-8 border-t-8 border-b-8 border-t-transparent border-b-transparent border-l-blue-800" />
              )}
            </div>
          </div>,
          document.body,
        )
      : null;

  return {
    triggerRef,
    show,
    hide,
    element: tooltip,
  };
}
